import React, {useEffect} from 'react';
import WorkspaceUI from '../view/WorkspaceUI';
import {useNavigate, useParams} from 'react-router-dom';
import {inject, observer} from 'mobx-react';
import WorkspaceStore from '../store/WorkspaceStore';
import {APP_PERMISSIONS} from '../../../data/permissions';
import NavigationStore from '../../../navigation/store/NavigationStore';
import {
  isBusinessSubscription,
  SUBSCRIPTION_ENTERPRISE,
  SUBSCRIPTION_TRIAL,
} from '../../Subscription/config';
import {ORGANIZATION_SUBSCRIPTION_ACTIVE} from '../../../data/models/Oganization/Organization';

interface Props {
  workspaceStore?: WorkspaceStore;
  navigationStore?: NavigationStore;
}

const WorkspaceContainer = inject(
  'workspaceStore',
  'navigationStore',
)(
  observer(({workspaceStore, navigationStore}: Props) => {
    const {organizationId} = useParams();
    const navigate = useNavigate();

    useEffect(() => {
      if (
        organizationId &&
        navigationStore!.subscriptionStatus === ORGANIZATION_SUBSCRIPTION_ACTIVE
      ) {
        workspaceStore!.fetchDashboardDetails(organizationId);
      } else {
        if (
          navigationStore!.organizationSubscription?.type !== SUBSCRIPTION_TRIAL
        )
          onSubscribe();
      }
    }, []);

    const onStartTrial = () => {
      navigate(`/subscription/${organizationId}/select_trial`);
    };

    const onSubscribe = () => {
      navigate(`/manage/${organizationId}/settings/subscription`);
    };

    const onBuyAddons = () => {
      navigate(`/${organizationId}/marketplace`);
    };

    const onCustomizeWorkspaceBranding = () => {
      navigate(`/manage/${organizationId}/settings/general`);
    };

    const onManageAccount = () => {
      navigate(`/manage/${organizationId}/settings/general`);
    };

    const onManageIntegrations = () => {
      navigate(`/manage/${organizationId}/settings/lti_settings`);
    };

    const onContactUs = () => {
      if (window) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        window
          .open(
            'https://help.tapybl.com/support/tickets/new?ticket_form=upgrade_plan',
            '_blank',
          )
          .focus();
      }
    };

    const onOpenAssistants = () => {
      navigate(`/manage/${organizationId}/home/ai_assistant`);
    };

    const hasTrialExpired = !!(
      navigationStore!.organizationSubscription?.type === SUBSCRIPTION_TRIAL &&
      navigationStore!.organizationSubscription.trialEnd &&
      new Date(navigationStore!.organizationSubscription.trialEnd).getTime() <
        new Date().getTime()
    );

    const allowAddingCreators =
      isBusinessSubscription(navigationStore!.organizationSubscription?.type) ||
      navigationStore!.organizationSubscription?.type ===
        SUBSCRIPTION_ENTERPRISE;

    return navigationStore!.userOrganization ? (
      <WorkspaceUI
        hasTrialExpired={hasTrialExpired}
        usedTrial={
          navigationStore!.userOrganization.trialSubscriptionUsed || false
        }
        onOpenAssistants={onOpenAssistants}
        onBuyAddons={onBuyAddons}
        isLoading={workspaceStore!.isLoading}
        dashboardData={workspaceStore!.dashboardData}
        subscriptionPlan={navigationStore!.organizationSubscription?.type}
        onSubscribe={onSubscribe}
        onStartTrial={onStartTrial}
        navigateCustomizeWorkspaceBranding={onCustomizeWorkspaceBranding}
        navigateManageAccount={onManageAccount}
        navigateManageIntegrations={onManageIntegrations}
        hasAdminPermissions={navigationStore!.userPermissions.includes(
          APP_PERMISSIONS.ORG_MANAGE_SETTINGS,
        )}
        organizationId={organizationId}
        onContactUs={onContactUs}
        subscriptionPermissions={navigationStore!.subscriptionPermissions}
        canNavigateToLtiSettings={
          navigationStore?.organizationSubscription !== undefined
        }
        allowAddingCreators={allowAddingCreators}
      />
    ) : null;
  }),
);

export default WorkspaceContainer;
